/**
 * InterplanetaryStarships - Highly detailed SpaceX Starship fleet
 * Route: Earth ↔ Mercury (Continuous round-trip)
 * Ultra-realistic Starship with authentic landing sequence
 * LANDING: Fast flip at last moment (like real SpaceX), land on surface, take off again
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

interface InterplanetaryStarshipsProps {
  earthPosition: [number, number, number];
  mercuryPosition: [number, number, number];
  shipCount?: number;
  // Callback to update ship tracking for camera
  onShipUpdate?: (index: number, position: THREE.Vector3, direction: THREE.Vector3) => void;
}

interface StarshipData {
  id: number;
  progress: number;
  speed: number;
  pathVariation: THREE.Vector3;
  scale: number;
  rotationOffset: number;
  goingToMercury: boolean;
  // Unique landing position offset to prevent collisions
  landingAngleOffset: number;
}

// Journey phases (0-1 represents one leg of the journey)
const LIFTOFF_END = 0.06;
const FLIP_START = 0.90;
const FLIP_END = 0.94;
const LANDING_BURN_START = 0.93;
const LEGS_DEPLOY = 0.96;
const TOUCHDOWN = 0.99;
const LANDED_TIME = 6.0;

// ============ PRE-ALLOCATED OBJECTS FOR PERFORMANCE ============
// These are reused every frame to avoid garbage collection
const _tempVec = new THREE.Vector3();
const _tempVec2 = new THREE.Vector3();
const _tempVec3 = new THREE.Vector3();
const _midPoint = new THREE.Vector3();
const _direction = new THREE.Vector3();
const _targetQuat = new THREE.Quaternion();
const _defaultUp = new THREE.Vector3(0, 1, 0);

export function InterplanetaryStarships({
  earthPosition,
  mercuryPosition,
  shipCount = 5,
  onShipUpdate
}: InterplanetaryStarshipsProps) {
  const shipsRef = useRef<StarshipData[]>([]);
  const { isClipMode } = useStore();

  // Initialize ship data - LANE-BASED SYSTEM (deterministic, no collisions)
  useMemo(() => {
    shipsRef.current = Array.from({ length: shipCount }, (_, i) => {
      // Each ship gets a unique lane based on ID
      const laneIndex = i % shipCount;
      const landingAngle = (laneIndex / shipCount) * Math.PI * 2;

      // Deterministic path variations based on lane - NO RANDOM
      // Each ship has a unique, fixed corridor
      const laneHeight = 4 + (laneIndex * 2.5); // Different heights per ship
      const laneOffset = laneIndex * 1.5; // Horizontal separation

      return {
        id: i,
        // Evenly distributed progress - ships spaced apart in time
        progress: (i / shipCount),
        // Same base speed for all - synchronized
        speed: 0.008,
        // Deterministic path variation based on lane
        pathVariation: new THREE.Vector3(
          Math.cos(landingAngle) * 5 + laneOffset * 0.3,
          laneHeight,
          Math.sin(landingAngle) * 5
        ),
        scale: 0.15,
        rotationOffset: landingAngle,
        goingToMercury: i % 2 === 0,
        landingAngleOffset: landingAngle
      };
    });
  }, [shipCount]);

  const earthPoint = useMemo(() => new THREE.Vector3(...earthPosition), [earthPosition]);
  const mercuryPoint = useMemo(() => new THREE.Vector3(...mercuryPosition), [mercuryPosition]);

  // HIDE during clip mode - only LaunchStarship and HeroStarship should be visible
  // These ships would clutter the cinematic sequence
  if (isClipMode) {
    return null;
  }

  return (
    <group>
      {shipsRef.current.map((ship) => (
        <DetailedStarship
          key={ship.id}
          shipData={ship}
          earthPoint={earthPoint}
          mercuryPoint={mercuryPoint}
          onShipUpdate={onShipUpdate}
        />
      ))}
    </group>
  );
}

function DetailedStarship({
  shipData,
  earthPoint,
  mercuryPoint,
  onShipUpdate
}: {
  shipData: StarshipData;
  earthPoint: THREE.Vector3;
  mercuryPoint: THREE.Vector3;
  onShipUpdate?: (index: number, position: THREE.Vector3, direction: THREE.Vector3) => void;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const shipBodyRef = useRef<THREE.Group>(null);
  const progressRef = useRef(shipData.progress);
  const goingToMercuryRef = useRef(shipData.goingToMercury);
  const landedTimerRef = useRef(0); // Time spent landed
  const flipProgressRef = useRef(0); // 0 = nose-forward, 1 = engines-forward (retrograde)
  const legDeployRef = useRef(0); // 0 = retracted, 1 = deployed
  const currentDirectionRef = useRef(new THREE.Vector3(1, 0, 0)); // For camera tracking
  const engineGlowRef = useRef<THREE.PointLight>(null);
  const landingBurnGlowRef = useRef<THREE.Group>(null);
  const exhaustPlumeRef = useRef<THREE.Group>(null);
  const boosterSepRef = useRef<THREE.Group>(null);
  const rcsRef = useRef<THREE.Group>(null);
  const legsRef = useRef<THREE.Group[]>([]);
  const isLiftingOffRef = useRef(false);

  // Calculate UNIQUE landing positions on planet surfaces per ship
  // Each ship lands at a different spot to avoid collisions
  const mercuryLandingPos = useMemo(() => {
    // Base direction from Mercury to Earth
    const toEarth = earthPoint.clone().sub(mercuryPoint).normalize();

    // Create perpendicular vectors for offset
    const up = new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(toEarth, up).normalize();
    const forward = new THREE.Vector3().crossVectors(right, toEarth).normalize();

    // Apply angular offset for this specific ship
    const offsetRadius = 1.5; // Spread ships around landing zone
    const offset = right.clone().multiplyScalar(Math.cos(shipData.landingAngleOffset) * offsetRadius)
      .add(forward.clone().multiplyScalar(Math.sin(shipData.landingAngleOffset) * offsetRadius));

    // Landing position on Mercury surface
    const landingDir = toEarth.clone().add(offset.multiplyScalar(0.3)).normalize();
    return mercuryPoint.clone().add(landingDir.multiplyScalar(2.2));
  }, [earthPoint, mercuryPoint, shipData.landingAngleOffset]);

  const earthLandingPos = useMemo(() => {
    // Base direction from Earth to Mercury
    const toMercury = mercuryPoint.clone().sub(earthPoint).normalize();

    // Create perpendicular vectors for offset
    const up = new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(toMercury, up).normalize();
    const forward = new THREE.Vector3().crossVectors(right, toMercury).normalize();

    // Apply angular offset for this specific ship (opposite side from Mercury landing)
    const offsetRadius = 1.2;
    const offset = right.clone().multiplyScalar(Math.cos(shipData.landingAngleOffset + Math.PI) * offsetRadius)
      .add(forward.clone().multiplyScalar(Math.sin(shipData.landingAngleOffset + Math.PI) * offsetRadius));

    // Landing position on Earth surface
    const landingDir = toMercury.clone().add(offset.multiplyScalar(0.3)).normalize();
    return earthPoint.clone().add(landingDir.multiplyScalar(1.8));
  }, [earthPoint, mercuryPoint, shipData.landingAngleOffset]);

  // Get start and end points based on direction (no allocation)
  const getStartEnd = () => {
    return goingToMercuryRef.current
      ? { start: earthLandingPos, end: mercuryLandingPos }
      : { start: mercuryLandingPos, end: earthLandingPos };
  };

  // Calculate point on path - writes to provided output vector (no allocation)
  const getPointOnPath = (t: number, out: THREE.Vector3) => {
    const { start, end } = getStartEnd();

    // Calculate midpoint in-place
    _midPoint.addVectors(start, end).multiplyScalar(0.5);
    _midPoint.y += 10 + shipData.pathVariation.y;
    _midPoint.x += shipData.pathVariation.x * 0.3;
    _midPoint.z += shipData.pathVariation.z * 0.3;

    // Quadratic bezier
    const t1 = 1 - t;
    out.set(
      t1 * t1 * start.x + 2 * t1 * t * _midPoint.x + t * t * end.x,
      t1 * t1 * start.y + 2 * t1 * t * _midPoint.y + t * t * end.y,
      t1 * t1 * start.z + 2 * t1 * t * _midPoint.z + t * t * end.z
    );
  };

  useFrame((state, delta) => {
    if (!groupRef.current || !shipBodyRef.current) return;

    const t = progressRef.current;
    const isLanded = t >= TOUCHDOWN;
    const isLanding = t >= FLIP_START;
    const isFlipping = t >= FLIP_START && t < FLIP_END;
    const isLandingBurn = t >= LANDING_BURN_START && t < TOUCHDOWN;

    // === STATE MACHINE ===
    if (isLanded) {
      // LANDED STATE - stay on surface
      landedTimerRef.current += delta;

      if (landedTimerRef.current > LANDED_TIME) {
        goingToMercuryRef.current = !goingToMercuryRef.current;
        progressRef.current = 0;
        landedTimerRef.current = 0;
        flipProgressRef.current = 0;
        legDeployRef.current = 0;
      } else {
        const { end } = getStartEnd();
        groupRef.current.position.copy(end);
        shipBodyRef.current.rotation.set(0, 0, 0);

        // Orient upright - use pre-allocated vectors
        const planetCenter = goingToMercuryRef.current ? mercuryPoint : earthPoint;
        _tempVec.subVectors(end, planetCenter).normalize(); // awayFromPlanet

        // Simple orientation using setFromUnitVectors
        _targetQuat.setFromUnitVectors(_defaultUp, _tempVec);
        groupRef.current.quaternion.copy(_targetQuat);

        legDeployRef.current = 1;
        if (engineGlowRef.current) engineGlowRef.current.intensity = 0.2;
        if (landingBurnGlowRef.current) landingBurnGlowRef.current.visible = false;
      }
    } else {
      // FLYING STATE
      const isLiftoff = t < LIFTOFF_END;
      isLiftingOffRef.current = isLiftoff;

      // Speed varies by phase
      let speedMult = t < 0.02 ? 0.15 + t * 5
        : t < LIFTOFF_END ? 0.4 + (t - 0.02) * 15
        : t > FLIP_START ? 0.2 : 1.0;

      progressRef.current = Math.min(progressRef.current + delta * shipData.speed * speedMult, 1.0);

      // Exhaust plume
      if (exhaustPlumeRef.current) {
        if (isLiftoff) {
          exhaustPlumeRef.current.visible = true;
          const li = Math.min(t / 0.03, 1);
          exhaustPlumeRef.current.scale.set(1 + li, 2 + li * 4, 1 + li);
        } else if (isLandingBurn) {
          exhaustPlumeRef.current.visible = true;
          exhaustPlumeRef.current.scale.set(1.2, 2.5, 1.2);
        } else if (t < FLIP_START) {
          exhaustPlumeRef.current.visible = true;
          exhaustPlumeRef.current.scale.set(0.5, 1, 0.5);
        } else {
          exhaustPlumeRef.current.visible = flipProgressRef.current < 0.3;
          exhaustPlumeRef.current.scale.set(0.3, 0.5, 0.3);
        }
      }

      // Get positions using pre-allocated vectors
      getPointOnPath(t, _tempVec);  // current position
      getPointOnPath(Math.min(t + 0.02, 0.999), _tempVec2);  // next position

      groupRef.current.position.copy(_tempVec);

      // Orientation using pre-allocated vectors
      _direction.subVectors(_tempVec2, _tempVec);
      if (_direction.lengthSq() > 0.000001) {
        _direction.normalize();
        currentDirectionRef.current.copy(_direction);

        // Flip direction if in retrograde
        if (flipProgressRef.current >= 0.5) _direction.negate();

        _targetQuat.setFromUnitVectors(_defaultUp, _direction);
        groupRef.current.quaternion.slerp(_targetQuat, 0.08);
      }
    }

    // === FLIP MANEUVER (fast, like real SpaceX) ===
    if (isFlipping) {
      // Quick flip over 3% of journey
      const flipT = (t - FLIP_START) / (FLIP_END - FLIP_START);
      // Use sine curve for realistic flip dynamics (slow-fast-slow)
      flipProgressRef.current = Math.sin(flipT * Math.PI / 2);
    } else if (t >= FLIP_END && t < TOUCHDOWN) {
      flipProgressRef.current = 1;
    } else if (t < FLIP_START) {
      flipProgressRef.current = 0;
    }

    // NOTE: Flip rotation now handled via quaternion slerp in orientation code above
    // No additional rotation needed on shipBody - keeps ship stable

    // === LANDING LEGS ===
    if (t >= LEGS_DEPLOY || isLanded) {
      const deployT = isLanded ? 1 : (t - LEGS_DEPLOY) / (TOUCHDOWN - LEGS_DEPLOY);
      legDeployRef.current = Math.min(deployT * 2, 1); // Fast deployment
    } else {
      legDeployRef.current = 0;
    }

    // Update leg angles
    legsRef.current.forEach((leg) => {
      if (leg) {
        // Legs rotate outward: retracted ~10°, deployed ~50°
        const deployAngle = 0.17 + legDeployRef.current * 0.7;
        leg.rotation.x = deployAngle;
      }
    });

    // === ENGINE EFFECTS ===
    if (engineGlowRef.current && !isLanded) {
      const pulse = 0.8 + Math.sin(state.clock.elapsedTime * 15 + shipData.id) * 0.2;
      const flicker = 0.9 + Math.sin(state.clock.elapsedTime * 47 + shipData.id * 3) * 0.1;

      if (t < LIFTOFF_END) {
        // MAXIMUM THRUST - liftoff (33 Raptor engines firing!)
        engineGlowRef.current.intensity = pulse * flicker * 8.0;
        engineGlowRef.current.color.setHex(0xff6622);
        engineGlowRef.current.distance = 15;
      } else if (isLandingBurn) {
        // Strong thrust - landing burn
        engineGlowRef.current.intensity = pulse * flicker * 6.0;
        engineGlowRef.current.color.setHex(0xff8844);
        engineGlowRef.current.distance = 12;
      } else if (isFlipping) {
        // Minimal during flip (RCS only)
        engineGlowRef.current.intensity = pulse * 0.5;
        engineGlowRef.current.color.setHex(0x88aaff);
        engineGlowRef.current.distance = 4;
      } else {
        // Cruise - vacuum Raptors, blue flame
        engineGlowRef.current.intensity = pulse * 2.0;
        engineGlowRef.current.color.setHex(0x6699ff);
        engineGlowRef.current.distance = 8;
      }
    }

    // Landing burn plume
    if (landingBurnGlowRef.current) {
      landingBurnGlowRef.current.visible = isLandingBurn;
      if (isLandingBurn) {
        const intensity = (t - LANDING_BURN_START) / (TOUCHDOWN - LANDING_BURN_START);
        landingBurnGlowRef.current.scale.setScalar(0.8 + intensity * 0.7);
      }
    }

    // === RCS THRUSTERS ===
    if (rcsRef.current) {
      if (isFlipping) {
        // Very active during flip
        rcsRef.current.rotation.y = state.clock.elapsedTime * 5 + shipData.rotationOffset;
      } else {
        rcsRef.current.rotation.y = state.clock.elapsedTime * 0.3 + shipData.rotationOffset;
      }
    }

    // === UPDATE CAMERA TRACKING ===
    if (onShipUpdate && groupRef.current) {
      // Reuse already-calculated position and direction from above
      if (flipProgressRef.current > 0.5) {
        _tempVec3.copy(_direction).negate();
      } else {
        _tempVec3.copy(_direction);
      }
      onShipUpdate(shipData.id, groupRef.current.position, _tempVec3);
    }
  });

  return (
    <group ref={groupRef} scale={shipData.scale}>
      {/* Ship body wrapper - this rotates during flip maneuver */}
      <group ref={shipBodyRef}>
        {/* ========== STARSHIP MAIN BODY ========== */}

        {/* Primary hull - Stainless steel cylinder */}
        <mesh position={[0, 0, 0]}>
          <cylinderGeometry args={[0.45, 0.45, 3.2, 24]} />
          <meshStandardMaterial
            color={0xf8f8f8}
            metalness={0.85}
            roughness={0.15}
          />
        </mesh>

      {/* Hull panel lines - horizontal rings */}
      {[-1.2, -0.6, 0, 0.6, 1.2].map((y, i) => (
        <mesh key={`ring-${i}`} position={[0, y, 0]}>
          <torusGeometry args={[0.452, 0.008, 8, 32]} />
          <meshStandardMaterial color={0xcccccc} metalness={0.9} roughness={0.1} />
        </mesh>
      ))}

      {/* Vertical weld lines */}
      {[0, Math.PI/3, Math.PI*2/3, Math.PI, Math.PI*4/3, Math.PI*5/3].map((angle, i) => (
        <mesh
          key={`weld-${i}`}
          position={[Math.cos(angle) * 0.451, 0, Math.sin(angle) * 0.451]}
          rotation={[0, -angle, 0]}
        >
          <boxGeometry args={[0.015, 3.2, 0.005]} />
          <meshStandardMaterial color={0xdddddd} metalness={0.7} roughness={0.3} />
        </mesh>
      ))}

      {/* ========== NOSE CONE ========== */}
      <group position={[0, 2.1, 0]}>
        {/* Main nose cone - ogive shape approximation */}
        <mesh position={[0, 0.4, 0]}>
          <coneGeometry args={[0.45, 1.2, 24]} />
          <meshStandardMaterial
            color={0xf5f5f5}
            metalness={0.8}
            roughness={0.2}
          />
        </mesh>

        {/* Nose tip */}
        <mesh position={[0, 1.05, 0]}>
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshStandardMaterial
            color={0xeeeeee}
            metalness={0.9}
            roughness={0.1}
          />
        </mesh>

        {/* Nose cone panel lines */}
        {[0.1, 0.3, 0.5, 0.7].map((y, i) => (
          <mesh key={`nose-ring-${i}`} position={[0, y, 0]}>
            <torusGeometry args={[0.45 - y * 0.3, 0.006, 6, 24]} />
            <meshStandardMaterial color={0xcccccc} metalness={0.8} roughness={0.2} />
          </mesh>
        ))}
      </group>

      {/* ========== HEAT SHIELD TILES (Black side) ========== */}
      <group>
        {/* Main heat shield coverage - windward side */}
        <mesh position={[0, 0.5, -0.25]} rotation={[0.15, 0, 0]}>
          <boxGeometry args={[0.7, 2.8, 0.02]} />
          <meshStandardMaterial
            color={0x1a1a1a}
            roughness={0.95}
            metalness={0.05}
          />
        </mesh>

        {/* Heat shield tile pattern - individual tiles */}
        {Array.from({ length: 8 }).map((_, row) =>
          Array.from({ length: 4 }).map((_, col) => (
            <mesh
              key={`tile-${row}-${col}`}
              position={[
                (col - 1.5) * 0.17,
                -1.0 + row * 0.35,
                -0.44
              ]}
            >
              <boxGeometry args={[0.15, 0.32, 0.02]} />
              <meshStandardMaterial
                color={0x111111}
                roughness={1}
                metalness={0}
              />
            </mesh>
          ))
        )}

        {/* Nose heat shield */}
        <mesh position={[0, 2.2, -0.2]} rotation={[0.4, 0, 0]}>
          <boxGeometry args={[0.5, 0.8, 0.02]} />
          <meshStandardMaterial color={0x1a1a1a} roughness={0.95} metalness={0.05} />
        </mesh>
      </group>

      {/* ========== FORWARD FLAPS (Canards) ========== */}
      {[
        { x: 0.52, z: 0, rotY: Math.PI/2 },
        { x: -0.52, z: 0, rotY: -Math.PI/2 }
      ].map((pos, i) => (
        <group key={`fwd-flap-${i}`} position={[pos.x, 1.8, pos.z]} rotation={[0, pos.rotY, 0.15]}>
          {/* Flap body */}
          <mesh>
            <boxGeometry args={[0.02, 0.5, 0.35]} />
            <meshStandardMaterial color={0x1a1a1a} roughness={0.9} metalness={0.1} />
          </mesh>
          {/* Flap hinge mechanism */}
          <mesh position={[0.03, 0, 0]}>
            <cylinderGeometry args={[0.03, 0.03, 0.3, 8]} />
            <meshStandardMaterial color={0x444444} metalness={0.8} roughness={0.3} />
          </mesh>
        </group>
      ))}

      {/* ========== AFT FLAPS (Main fins) ========== */}
      {[
        { x: 0.52, z: 0, rotY: Math.PI/2 },
        { x: -0.52, z: 0, rotY: -Math.PI/2 }
      ].map((pos, i) => (
        <group key={`aft-flap-${i}`} position={[pos.x, -0.8, pos.z]} rotation={[0, pos.rotY, 0.12]}>
          {/* Main flap body - larger than forward */}
          <mesh>
            <boxGeometry args={[0.025, 0.9, 0.5]} />
            <meshStandardMaterial color={0x1a1a1a} roughness={0.9} metalness={0.1} />
          </mesh>
          {/* Actuator housing */}
          <mesh position={[0.04, 0.2, 0]}>
            <boxGeometry args={[0.06, 0.4, 0.15]} />
            <meshStandardMaterial color={0x333333} metalness={0.7} roughness={0.4} />
          </mesh>
          {/* Hinge line */}
          <mesh position={[0.02, 0, 0]}>
            <cylinderGeometry args={[0.025, 0.025, 0.45, 8]} />
            <meshStandardMaterial color={0x444444} metalness={0.8} roughness={0.3} />
          </mesh>
        </group>
      ))}

      {/* ========== ENGINE SECTION ========== */}
      <group position={[0, -1.8, 0]}>
        {/* Engine skirt - tapered section */}
        <mesh>
          <cylinderGeometry args={[0.42, 0.52, 0.4, 24]} />
          <meshStandardMaterial color={0x888888} metalness={0.7} roughness={0.4} />
        </mesh>

        {/* Engine bay dark interior */}
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.48, 0.48, 0.15, 24]} />
          <meshStandardMaterial color={0x222222} metalness={0.3} roughness={0.8} />
        </mesh>

        {/* ========== RAPTOR ENGINES - 3 Sea Level + 3 Vacuum ========== */}
        {/* Center cluster - 3 Sea Level Raptors */}
        {[0, (Math.PI * 2) / 3, (Math.PI * 4) / 3].map((angle, i) => (
          <group key={`raptor-sl-${i}`} position={[Math.cos(angle) * 0.18, -0.35, Math.sin(angle) * 0.18]}>
            {/* Engine bell - Sea Level (compact) */}
            <mesh>
              <coneGeometry args={[0.12, 0.25, 16, 1, true]} />
              <meshStandardMaterial
                color={0x3a3a3a}
                metalness={0.95}
                roughness={0.1}
                side={THREE.DoubleSide}
              />
            </mesh>
            {/* Combustion chamber */}
            <mesh position={[0, 0.15, 0]}>
              <cylinderGeometry args={[0.06, 0.08, 0.15, 12]} />
              <meshStandardMaterial color={0x555555} metalness={0.8} roughness={0.3} />
            </mesh>
            {/* Engine glow */}
            <mesh position={[0, -0.18, 0]}>
              <coneGeometry args={[0.1, 0.4, 12]} />
              <meshBasicMaterial
                color={0x88ccff}
                transparent
                opacity={0.85}
                blending={THREE.AdditiveBlending}
              />
            </mesh>
          </group>
        ))}

        {/* Outer ring - 3 Vacuum Raptors (larger bells) */}
        {[Math.PI / 3, Math.PI, (Math.PI * 5) / 3].map((angle, i) => (
          <group key={`raptor-vac-${i}`} position={[Math.cos(angle) * 0.35, -0.4, Math.sin(angle) * 0.35]}>
            {/* Vacuum engine bell - extended nozzle */}
            <mesh>
              <coneGeometry args={[0.15, 0.35, 16, 1, true]} />
              <meshStandardMaterial
                color={0x2a2a2a}
                metalness={0.95}
                roughness={0.1}
                side={THREE.DoubleSide}
              />
            </mesh>
            {/* Regenerative cooling channels (visual detail) */}
            <mesh position={[0, 0.05, 0]}>
              <torusGeometry args={[0.12, 0.01, 6, 24]} />
              <meshStandardMaterial color={0x444444} metalness={0.9} roughness={0.2} />
            </mesh>
            {/* Engine glow */}
            <mesh position={[0, -0.22, 0]}>
              <coneGeometry args={[0.12, 0.5, 12]} />
              <meshBasicMaterial
                color={0x66aaff}
                transparent
                opacity={0.8}
                blending={THREE.AdditiveBlending}
              />
            </mesh>
          </group>
        ))}

        {/* Main engine light */}
        <pointLight
          ref={engineGlowRef}
          position={[0, -0.5, 0]}
          color={0x88ccff}
          intensity={1.5}
          distance={4}
        />
      </group>

      {/* ========== RCS THRUSTERS ========== */}
      <group ref={rcsRef}>
        {[0, Math.PI/2, Math.PI, Math.PI*3/2].map((angle, i) => (
          <group
            key={`rcs-${i}`}
            position={[Math.cos(angle) * 0.48, 1.5, Math.sin(angle) * 0.48]}
            rotation={[0, -angle, 0]}
          >
            {/* RCS pod */}
            <mesh>
              <boxGeometry args={[0.08, 0.12, 0.06]} />
              <meshStandardMaterial color={0x666666} metalness={0.7} roughness={0.4} />
            </mesh>
            {/* Thruster nozzles */}
            <mesh position={[0.045, 0, 0]} rotation={[0, 0, Math.PI/2]}>
              <coneGeometry args={[0.02, 0.04, 6]} />
              <meshStandardMaterial color={0x444444} metalness={0.8} roughness={0.3} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ========== CARGO BAY DETAILS ========== */}
      <group position={[0, 0.3, 0.35]}>
        {/* Cargo door outline */}
        <mesh>
          <boxGeometry args={[0.5, 1.2, 0.02]} />
          <meshStandardMaterial color={0xe8e8e8} metalness={0.8} roughness={0.2} />
        </mesh>
        {/* Door seam */}
        <mesh position={[0, 0, 0.01]}>
          <planeGeometry args={[0.48, 0.01]} />
          <meshStandardMaterial color={0x999999} />
        </mesh>
        {/* Payload window (small) */}
        <mesh position={[0, 0.3, 0.015]}>
          <circleGeometry args={[0.08, 16]} />
          <meshStandardMaterial
            color={0x334455}
            metalness={0.3}
            roughness={0.5}
            transparent
            opacity={0.8}
          />
        </mesh>
      </group>

      {/* ========== HEADER TANKS (visible bulge) ========== */}
      <mesh position={[0, 1.4, 0]}>
        <sphereGeometry args={[0.38, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={0xf0f0f0} metalness={0.85} roughness={0.15} />
      </mesh>

      {/* ========== SPACEX LOGO AREA ========== */}
      <mesh position={[0, 0.8, 0.46]} rotation={[0, 0, 0]}>
        <planeGeometry args={[0.4, 0.08]} />
        <meshStandardMaterial color={0x1a1a1a} roughness={0.5} />
      </mesh>

      {/* ========== DOCKING PORT ========== */}
      <group position={[0, 2.95, 0]}>
        <mesh>
          <cylinderGeometry args={[0.12, 0.1, 0.08, 16]} />
          <meshStandardMaterial color={0xdddddd} metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh position={[0, 0.05, 0]}>
          <torusGeometry args={[0.1, 0.015, 8, 16]} />
          <meshStandardMaterial color={0xaaaaaa} metalness={0.9} roughness={0.1} />
        </mesh>
      </group>

        {/* ========== NAVIGATION LIGHTS ========== */}
        {/* Port (red) */}
        <mesh position={[-0.48, 0.5, 0]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshBasicMaterial color={0xff0000} />
        </mesh>
        <pointLight position={[-0.48, 0.5, 0]} color={0xff0000} intensity={0.3} distance={1} />

        {/* Starboard (green) */}
        <mesh position={[0.48, 0.5, 0]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshBasicMaterial color={0x00ff00} />
        </mesh>
        <pointLight position={[0.48, 0.5, 0]} color={0x00ff00} intensity={0.3} distance={1} />

        {/* Tail (white) */}
        <mesh position={[0, -1.5, 0.48]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshBasicMaterial color={0xffffff} />
        </mesh>

        {/* ========== LANDING LEGS (6 legs around base) ========== */}
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const angle = (i * Math.PI * 2) / 6;
          return (
            <group
              key={`leg-${i}`}
              ref={(el) => { if (el) legsRef.current[i] = el; }}
              position={[
                Math.cos(angle) * 0.42,
                -1.6,
                Math.sin(angle) * 0.42
              ]}
              rotation={[0.25, angle, 0]} // Initial angle (retracted)
            >
              {/* Leg strut - main structural member */}
              <mesh position={[0, -0.4, 0]}>
                <boxGeometry args={[0.04, 0.9, 0.06]} />
                <meshStandardMaterial
                  color={0x555555}
                  metalness={0.8}
                  roughness={0.3}
                />
              </mesh>

              {/* Secondary strut (angled brace) */}
              <mesh position={[0.06, -0.25, 0]} rotation={[0, 0, 0.4]}>
                <boxGeometry args={[0.025, 0.5, 0.04]} />
                <meshStandardMaterial
                  color={0x444444}
                  metalness={0.7}
                  roughness={0.4}
                />
              </mesh>

              {/* Foot pad - wide base for landing */}
              <mesh position={[0, -0.9, 0]}>
                <cylinderGeometry args={[0.12, 0.15, 0.04, 8]} />
                <meshStandardMaterial
                  color={0x333333}
                  metalness={0.6}
                  roughness={0.5}
                />
              </mesh>

              {/* Crush core indicator */}
              <mesh position={[0, -0.85, 0]}>
                <cylinderGeometry args={[0.03, 0.03, 0.08, 6]} />
                <meshStandardMaterial
                  color={0xff6600}
                  metalness={0.4}
                  roughness={0.6}
                />
              </mesh>

              {/* Hinge mechanism */}
              <mesh position={[0, 0, 0]}>
                <cylinderGeometry args={[0.04, 0.04, 0.1, 8]} />
                <meshStandardMaterial
                  color={0x666666}
                  metalness={0.85}
                  roughness={0.2}
                />
              </mesh>

              {/* Hydraulic actuator */}
              <mesh position={[-0.05, -0.2, 0]} rotation={[0, 0, -0.2]}>
                <cylinderGeometry args={[0.015, 0.02, 0.35, 8]} />
                <meshStandardMaterial
                  color={0x888888}
                  metalness={0.9}
                  roughness={0.1}
                />
              </mesh>
            </group>
          );
        })}

        {/* ========== LANDING BURN PLUME (extra intensity during landing) ========== */}
        <group ref={landingBurnGlowRef} position={[0, -2.3, 0]} visible={false}>
          {/* Massive exhaust plume during landing burn */}
          <mesh>
            <coneGeometry args={[0.35, 1.8, 16]} />
            <meshBasicMaterial
              color={0xffaa44}
              transparent
              opacity={0.7}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
          {/* Inner hot core */}
          <mesh position={[0, 0.2, 0]}>
            <coneGeometry args={[0.2, 1.2, 12]} />
            <meshBasicMaterial
              color={0xffffff}
              transparent
              opacity={0.9}
              blending={THREE.AdditiveBlending}
            />
          </mesh>
          {/* Shock diamonds */}
          {[0.3, 0.5, 0.7].map((y, i) => (
            <mesh key={`shock-${i}`} position={[0, -y, 0]}>
              <sphereGeometry args={[0.08 - i * 0.015, 8, 8]} />
              <meshBasicMaterial
                color={0xffcc88}
                transparent
                opacity={0.6 - i * 0.15}
                blending={THREE.AdditiveBlending}
              />
            </mesh>
          ))}
          {/* Landing burn light */}
          <pointLight color={0xffaa44} intensity={5} distance={8} />
        </group>

        {/* ========== MAIN EXHAUST PLUME (visible during all flight) ========== */}
        <group ref={exhaustPlumeRef} position={[0, -2.2, 0]}>
          {/* Outer plume - orange/yellow */}
          <mesh>
            <coneGeometry args={[0.4, 2.5, 16]} />
            <meshBasicMaterial
              color={0xff6622}
              transparent
              opacity={0.6}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
          {/* Mid plume - white hot */}
          <mesh position={[0, 0.3, 0]}>
            <coneGeometry args={[0.25, 1.8, 12]} />
            <meshBasicMaterial
              color={0xffddaa}
              transparent
              opacity={0.75}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
          {/* Inner core - blue (vacuum raptor) */}
          <mesh position={[0, 0.5, 0]}>
            <coneGeometry args={[0.15, 1.2, 12]} />
            <meshBasicMaterial
              color={0x6699ff}
              transparent
              opacity={0.9}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
          {/* Shock diamonds in exhaust */}
          {[0.4, 0.7, 1.0, 1.3].map((y, i) => (
            <mesh key={`exh-shock-${i}`} position={[0, -y, 0]}>
              <sphereGeometry args={[0.1 - i * 0.02, 8, 8]} />
              <meshBasicMaterial
                color={0xffaa66}
                transparent
                opacity={0.5 - i * 0.1}
                blending={THREE.AdditiveBlending}
                toneMapped={false}
              />
            </mesh>
          ))}
        </group>

        {/* ========== BOOSTER SEPARATION EFFECT (hot staging flash) ========== */}
        <group ref={boosterSepRef} position={[0, -2.5, 0]} visible={false}>
          {/* Separation ring flash */}
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.6, 0.15, 8, 24]} />
            <meshBasicMaterial
              color={0xffaa00}
              transparent
              opacity={0.9}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
          {/* Expanding gas cloud */}
          <mesh>
            <sphereGeometry args={[0.8, 16, 16]} />
            <meshBasicMaterial
              color={0xff8844}
              transparent
              opacity={0.5}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
          {/* Flash light */}
          <pointLight color={0xffaa00} intensity={10} distance={15} />
        </group>

      </group> {/* End of shipBodyRef */}
    </group>
  );
}
